/**
 * CalculatorTutorial — immersive slide-over guide for calculator pages.
 *
 * Renders a compact "Show tour" button in-line and, when opened, slides in a
 * large right-side panel with step navigation, visual illustrations and
 * dot progress. Users can leave it open while filling in fields, or dismiss
 * once and never see it again (per-calculator localStorage key).
 *
 * Usage:
 *   <CalculatorTutorial
 *     storageKey="business-calc-v1"
 *     title="How the Business Calculator works"
 *     steps={[{ title, body, illustration? }, ...]}
 *   />
 */
import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import {
  GraduationCap,
  X,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Package,
  Coins,
  BarChart3,
  Save,
} from 'lucide-react';

// Default step icons — fallback illustration when a step doesn't supply one.
const DEFAULT_ICONS = [Sparkles, Package, Coins, BarChart3, Save];

const CalculatorTutorial = ({ storageKey, title = 'Quick tour', reopenLabel = 'Show tour', steps = [] }) => {
  // Auto-opens on the very first visit only. Subsequent visits render the
  // compact "Show tour" trigger; the user opts back in explicitly.
  // Persisted per-page via a localStorage key so tours don't hijack clicks
  // on admin/portal cards (or any other page) after the first view.
  const lsKey = storageKey ? `fda:tutorial-dismissed:${storageKey}` : null;
  const initiallyOpen = (() => {
    if (!lsKey || typeof window === 'undefined') return true;
    try { return !window.localStorage.getItem(lsKey); }
    catch (_) { return true; }
  })();
  const [open, setOpen] = useState(initiallyOpen);
  const [step, setStep] = useState(0);

  // Lock body scroll when the slide-over is open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!steps.length) return null;

  const dismiss = () => {
    setOpen(false);
    if (lsKey && typeof window !== 'undefined') {
      try { window.localStorage.setItem(lsKey, '1'); } catch (_) { /* noop */ }
    }
  };

  const reopen = () => {
    setStep(0);
    setOpen(true);
  };

  const current = steps[Math.min(step, steps.length - 1)];
  const StepIcon = current.icon || DEFAULT_ICONS[step % DEFAULT_ICONS.length] || GraduationCap;

  return (
    <>
      {/* Trigger button — always available in-page */}
      <div className="mb-4" data-testid="tutorial-trigger">
        <Button
          onClick={reopen}
          variant="outline"
          size="sm"
          className="rounded-full font-semibold border-primary/40 text-foreground hover:border-primary hover:bg-primary/5"
          data-testid="tutorial-reopen-btn"
        >
          <GraduationCap className="w-4 h-4 mr-2 text-primary" />
          {reopenLabel}
        </Button>
      </div>

      {/* Slide-over overlay */}
      {open && (
        <div
          className="fixed inset-0 z-50 no-print"
          role="dialog"
          aria-modal="true"
          aria-labelledby="calc-tutorial-title"
          data-testid="calculator-tutorial"
        >
          {/* Backdrop — click to dismiss */}
          <button
            type="button"
            aria-label="Close tutorial"
            onClick={dismiss}
            className="absolute inset-0 bg-foreground/30 backdrop-blur-[2px] cursor-pointer"
            data-testid="tutorial-backdrop"
          />

          {/* Panel */}
          <aside
            className="absolute inset-y-0 right-0 w-full sm:w-[440px] lg:w-[500px] bg-background shadow-2xl border-l border-border flex flex-col animate-in slide-in-from-right-8 duration-300"
            data-testid="tutorial-panel"
          >
            {/* Header */}
            <header className="flex items-start justify-between gap-3 p-6 border-b border-border">
              <div className="min-w-0">
                <div className="text-xs font-bold uppercase tracking-widest text-primary">
                  {title}
                </div>
                <h2 id="calc-tutorial-title" className="mt-1 font-heading text-2xl font-extrabold tracking-tight text-foreground">
                  Step {step + 1} of {steps.length}
                </h2>
              </div>
              <button
                type="button"
                onClick={dismiss}
                className="p-2 -m-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex-shrink-0"
                aria-label="Dismiss tour"
                data-testid="tutorial-dismiss-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </header>

            {/* Body — scrollable */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Illustration block */}
              <div className="rounded-3xl bg-primary/10 border border-primary/20 aspect-[4/3] flex items-center justify-center relative overflow-hidden">
                <div className="absolute -top-8 -left-8 h-28 w-28 rounded-full bg-primary/20 blur-2xl" />
                <div className="absolute -bottom-8 -right-8 h-32 w-32 rounded-full bg-primary/30 blur-2xl" />
                <div className="relative flex flex-col items-center gap-3 text-center px-6">
                  <div className="h-20 w-20 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center shadow-lg">
                    <StepIcon className="w-10 h-10" />
                  </div>
                  <div className="font-heading text-primary text-sm font-extrabold uppercase tracking-widest">
                    Step {step + 1}
                  </div>
                </div>
              </div>

              {/* Step content */}
              <div>
                <h3 className="font-heading text-xl sm:text-2xl font-extrabold tracking-tight text-foreground">
                  {current.title}
                </h3>
                <p className="mt-3 text-base text-foreground/80 leading-relaxed">
                  {current.body}
                </p>
                {current.tip && (
                  <div className="mt-4 rounded-2xl bg-primary/5 border border-primary/20 p-4">
                    <div className="text-xs font-bold uppercase tracking-widest text-primary mb-1">Pro tip</div>
                    <p className="text-sm text-foreground/85 leading-relaxed">{current.tip}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer — nav */}
            <footer className="border-t border-border p-4 space-y-3">
              {/* Dot progress */}
              <div className="flex items-center gap-1.5 justify-center" aria-label="Tutorial progress">
                {steps.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setStep(i)}
                    aria-label={`Go to step ${i + 1}`}
                    data-testid={`tutorial-dot-${i}`}
                    className={`h-2 rounded-full transition-all ${
                      i === step ? 'w-8 bg-primary' : 'w-2 bg-border hover:bg-primary/40'
                    }`}
                  />
                ))}
              </div>
              <div className="flex items-center justify-between gap-3">
                <Button
                  variant="ghost"
                  disabled={step === 0}
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  className="rounded-full font-semibold"
                  data-testid="tutorial-prev-btn"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                {step < steps.length - 1 ? (
                  <Button
                    onClick={() => setStep((s) => s + 1)}
                    className="rounded-full font-semibold px-6"
                    data-testid="tutorial-next-btn"
                  >
                    Next <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button
                    onClick={dismiss}
                    className="rounded-full font-semibold px-6"
                    data-testid="tutorial-finish-btn"
                  >
                    Got it — let&apos;s go
                  </Button>
                )}
              </div>
            </footer>
          </aside>
        </div>
      )}
    </>
  );
};

export default CalculatorTutorial;
